import React from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * Add a new user to a specific tenant.
 *
 * Two mutually-exclusive paths: send secure invite email (default) OR provide
 * an initial password directly. Uses `INVITE_EXPIRY_OPTIONS` and `ROLES` from
 * the parent so the copy stays consistent with the rest of the app.
 */
export const CreateUserDialog = ({
  open, onOpenChange, tenantName, form, setForm, saving, onSubmit,
  inviteExpiryOptions, roles,
}) => {
  const passwordValid = form.send_invite || form.password.length >= 8;
  const canSubmit = !saving && form.name && form.email && passwordValid;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-50 border-slate-200 text-slate-900">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Adicionar Utilizador</DialogTitle>
          <DialogDescription className="text-slate-500">
            Criar utilizador para {tenantName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-slate-600">Nome</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Nome completo"
              className="bg-white border-slate-300 text-slate-900"
            />
          </div>
          <div>
            <Label className="text-slate-600">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              placeholder="email@empresa.com"
              className="bg-white border-slate-300 text-slate-900"
            />
          </div>
          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
            <div>
              <Label className="text-slate-900">Enviar convite por email</Label>
              <p className="text-xs text-slate-500 mt-0.5">O utilizador define a própria password através de um link seguro</p>
            </div>
            <Switch
              checked={form.send_invite}
              onCheckedChange={(v) => setForm(prev => ({ ...prev, send_invite: v }))}
              data-testid="create-user-invite-switch"
            />
          </div>
          {form.send_invite && (
            <div>
              <Label className="text-slate-600">Validade do link de convite</Label>
              <Select value={String(form.invite_expiry_hours)} onValueChange={(v) => setForm(prev => ({ ...prev, invite_expiry_hours: parseInt(v) }))}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="create-user-expiry-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-300">
                  {inviteExpiryOptions.map(opt => (
                    <SelectItem key={opt.value} value={String(opt.value)} className="text-slate-900 hover:bg-slate-100">{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {!form.send_invite && (
            <div>
              <Label className="text-slate-600">Password (mín. 8 caracteres)</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Password inicial"
                className="bg-white border-slate-300 text-slate-900"
                data-testid="create-user-password-input"
              />
            </div>
          )}
          <div>
            <Label className="text-slate-600">Perfil</Label>
            <Select value={form.role} onValueChange={(v) => setForm(prev => ({ ...prev, role: v }))}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-300">
                {roles.map(role => (
                  <SelectItem key={role.value} value={role.value} className="text-slate-900 hover:bg-slate-700">{role.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300 text-slate-600 hover:bg-slate-700">
            Cancelar
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit} className="bg-indigo-700 hover:bg-amber-600 text-black">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {form.send_invite ? 'Criar e enviar convite' : 'Criar Utilizador'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
